import { useState, useEffect, useMemo, useRef } from 'react'
import api from '../api'
import { TutorPickerCard, pluralRu } from './TutorPicker'

// Окно записи на урок — одно на звонильщиц (CallerDashboard), переписчиц и сопроводителей
// (LessonBookingBlock, SupportDashboard, TrialsBoard). 17.09 перерисовано по макету Антона
// (crm.ctg.school/preview/_tutor-picker-v2-preview.html): сначала менеджер нажимает день и час,
// под часом видно, сколько преподавателей свободны, ниже — только они, во всю ширину.
// Предмет и класс берутся из сделки, «изменить» открывает кнопки. Подтверждение препода
// после записи менеджером больше не требуется (платформа, 17.09) — урок сразу в расписании.

// ─────────── Icons ───────────
const I = ({ size = 16, children, className = '', stroke = 'currentColor', fill = 'none', strokeWidth = 1.6 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth={strokeWidth}
       strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>
)
const IconX = (p) => <I {...p}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></I>
const IconCheck = (p) => <I {...p}><path d="M20 6 9 17l-5-5"/></I>
const IconChevron = (p) => <I {...p}><path d="m9 18 6-6-6-6"/></I>
const IconSearch = (p) => <I {...p}><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></I>
const IconUser = (p) => <I {...p}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></I>

// Платформа поддерживает каталог по 13 предметам (см. katangens-platform/tutors/photos.ts,
// тип Subject) — лид-предмет маппим на них
const PLATFORM_SUBJECTS = ['Математика', 'Русский', 'Физика', 'Химия', 'Английский', 'Биология',
  'Обществознание', 'История', 'Информатика', 'География', 'Окружающий мир', 'Немецкий', 'Психология']

// Телефон преподавателя приходит из платформы в разном виде: «+7 (908) 667-44-19»,
// «9196025945», «79913251920». Приводим к единому читаемому формату и делаем
// кликабельным — звонильщице надо звонить преподу прямо отсюда (Антон 03.08).
export function formatTeacherPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  const ten = d.length >= 10 ? d.slice(-10) : ''
  if (!ten) return null
  return '+7 (' + ten.slice(0, 3) + ') ' + ten.slice(3, 6) + '-' + ten.slice(6, 8) + '-' + ten.slice(8)
}
export function telHref(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  const ten = d.length >= 10 ? d.slice(-10) : ''
  return ten ? 'tel:+7' + ten : null
}

// Фильтр по классу/уровню препода
const CLASS_FILTERS = ['Все', '1-4', '5-8', 'ОГЭ', 'ЕГЭ']
function teacherMatchesClass(t, cf) {
  if (cf === 'Все') return true
  const lv = t.classLevels || []
  // Точный класс из сделки (Антон 21.09: «нужен 8 класс — показывай только тех, у кого в анкете 8 класс,
  // а не всех 5–8 и тем более 1–4»). Старшие классы преподы отмечают галочками ОГЭ/ЕГЭ, а не цифрами:
  // 9 → «9» или ОГЭ, 10/11 → цифра или ЕГЭ.
  if (/^\d{1,2}$/.test(cf)) {
    const n = Number(cf)
    if (lv.includes(cf)) return true
    if (n === 9) return lv.includes('ОГЭ')
    if (n >= 10) return lv.includes('ЕГЭ')
    return false
  }
  if (cf === '1-4') return lv.some(x => ['1', '2', '3', '4'].includes(x))
  if (cf === '5-8') return lv.some(x => ['5', '6', '7', '8'].includes(x))
  return lv.includes(cf)   // ОГЭ / ЕГЭ
}
// Класс из сделки → фильтр по умолчанию (Антон 21.09: выбрали 11 класс, а в списке преподы 1–4).
// Старшие классы преподы отмечают галочками ОГЭ/ЕГЭ, а не цифрами (9 ↔ ОГЭ, 10–11 ↔ ЕГЭ — то же
// правило, что teacherLevelFits на платформе). Непонятный класс — «Все», как было.
function classFilterFor(classNum) {
  const s = String(classNum || '').trim().toLowerCase()
  if (/огэ/.test(s)) return 'ОГЭ'
  if (/егэ/.test(s)) return 'ЕГЭ'
  const m = s.match(/^(\d{1,2})/)
  if (!m) return 'Все'
  const n = Number(m[1])
  return n >= 1 && n <= 11 ? String(n) : 'Все'   // точный класс; 9–11 сопоставляются с ОГЭ/ЕГЭ в teacherMatchesClass
}

// Те же возрастные планки, что менеджер выбирает в форме скидки (CallerDashboard
// DISCOUNT_AGE_OPTS) — нужно, чтобы список преподов после скидки показывал ТОЛЬКО
// препода нужного возраста, а не любого (иначе цена и назначенный препод расходятся).
const AGE_BRACKET_LABELS = { 0: 'до 23 лет', 100: '23–30 лет', 200: '30–45 лет', 300: '45 лет и старше' }
function teacherAgeBracket(age) {
  if (age == null) return 100   // неизвестный возраст — тот же дефолт, что на бэке (ageSurchargeFor)
  const a = Number(age)
  if (a < 23) return 0
  if (a < 30) return 100
  if (a < 45) return 200
  return 300
}

// Цена 1200/1400/1600 по классу — тариф внутренних преподов («Лидеров»), НА ЛЮБОМ
// предмете (Антон 13.07: «1200/1400/1600 должна быть на лидерах, какой бы предмет
// они не вели»). Обычный препод физически не может столько стоить — если клиенту
// названа именно эта цена, список ниже должен показывать только их, независимо от
// текста предмета.
function leaderPriceForGrade(classNum) {
  const s = String(classNum || '').trim()
  const m = s.match(/^(\d{1,2})/)
  if (m) {
    const n = Number(m[1])
    if (n === 9) return 1400
    if (n >= 10) return 1600
    return 1200
  }
  if (/колледж|техникум/i.test(s)) return 1600
  return 1200
}

function mapSubject(s) {
  const x = String(s || '').toLowerCase()
  if (/матем/.test(x)) return 'Математика'
  if (/рус/.test(x)) return 'Русский'
  if (/физ/.test(x)) return 'Физика'
  if (/хим/.test(x)) return 'Химия'
  if (/англ/.test(x)) return 'Английский'
  if (/биолог/.test(x)) return 'Биология'
  if (/психолог/.test(x)) return 'Психология'
  if (/обществ/.test(x)) return 'Обществознание'
  if (/истори/.test(x)) return 'История'
  if (/информат/.test(x)) return 'Информатика'
  if (/географ/.test(x)) return 'География'
  if (/окружающ/.test(x)) return 'Окружающий мир'
  if (/немецк/.test(x)) return 'Немецкий'
  return 'Математика'
}

// ─────────── Неделя ───────────
const DOW_SHORT = ['ПН','ВТ','СР','ЧТ','ПТ','СБ','ВС']
const DOW_GEN = ['понедельник', 'вторник', 'среду', 'четверг', 'пятницу', 'субботу', 'воскресенье']
const MONTH_SHORT = ['янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек']
function localIso(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}
function buildWeek(anchor) {
  const d = new Date(anchor)
  const dow = (d.getDay() + 6) % 7
  const monday = new Date(d); monday.setDate(d.getDate() - dow); monday.setHours(0,0,0,0)
  const days = []
  const today = new Date(); today.setHours(0,0,0,0)
  for (let i = 0; i < 7; i++) {
    const dt = new Date(monday); dt.setDate(monday.getDate() + i)
    days.push({ dow: DOW_SHORT[i], date: `${dt.getDate()} ${MONTH_SHORT[dt.getMonth()]}`, iso: localIso(dt), today: dt.getTime() === today.getTime(), past: dt.getTime() < today.getTime() })
  }
  const range = `${days[0].date} — ${days[6].date} ${new Date(days[6].iso).getFullYear()}`
  return { monday, days, range }
}

const PILL = { fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 999, whiteSpace: 'nowrap', border: '1px solid #E2E8F0', background: '#fff', color: '#334155' }
const PILL_BLUE = { ...PILL, background: '#F0F9FF', color: '#0369A1', borderColor: '#BAE6FD' }
const PILL_ON = { ...PILL, background: '#38BDF8', color: '#fff', borderColor: '#38BDF8' }

const SORTS = [
  { key: 'rank', label: 'Сначала лучшие по приоритету' },
  { key: 'slots', label: 'Сначала у кого больше окон' },
  { key: 'price', label: 'Сначала дешевле' },
  { key: 'all', label: 'Все (и Лидеры, и обычные)' },
]

// ─────────── Компонент ───────────
// title/priceBadge/submitLabel — окно переиспользуется для записи на платный урок
// из карточки сопроводителя (Роман 23.09.2026): там своя шапка, своя цена и своя
// кнопка, а подбор преподавателя и неделя окон — те же, что на пробном.
export default function TrialSignupModal({ open, onClose, clientLabel, initial, onSubmit, showAllTeachers, title, priceBadge, submitLabel, freeTrialOption }) {
  const [weekAnchor, setWeekAnchor] = useState(() => new Date())
  const week = useMemo(() => buildWeek(weekAnchor), [weekAnchor])

  const [subject, setSubject] = useState('Математика')
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)

  const [search, setSearch] = useState('')
  const [classFilter, setClassFilter] = useState('Все')          // фильтр по классу/уровню
  const [classTouched, setClassTouched] = useState(false)        // менеджер сам нажал кнопку класса
  const [editFilters, setEditFilters] = useState(false)          // «изменить» — показать кнопки предмета и класса
  const [sortKey, setSortKey] = useState('rank')
  const [selDay, setSelDay] = useState(null)                     // индекс дня недели 0..6
  const [selTime, setSelTime] = useState(null)                   // '18:00'
  const [selectedTeacher, setSelectedTeacher] = useState(null)   // объект препода
  const [freeTrial, setFreeTrial] = useState(false)   // «Пробное бесплатно» (психология)
  const [profileTutor, setProfileTutor] = useState(null)         // карточка препода
  // Выбор окна из карточки препода (неделя целиком, Светлана 17.09): если день в другой неделе —
  // переключаем неделю и применяем выбор после перезагрузки списка
  const pendingPickRef = useRef(null)
  const keepTeacherRef = useRef(null)   // при листании недель выбранный препод остаётся выбранным

  const [comment, setComment]   = useState(initial?.comment || '')

  // Стартовый предмет — из анкеты клиента
  useEffect(() => {
    if (open) setSubject(mapSubject(initial?.subject))
  }, [open, initial?.subject])

  // По умолчанию выбран сегодняшний день (если неделя текущая), иначе понедельник
  useEffect(() => {
    if (!open) return
    const pk = pendingPickRef.current
    if (pk) {
      const i = week.days.findIndex(d => d.iso === pk.date)
      if (i >= 0) { setSelDay(i); setSelTime(pk.time); return }
    }
    const idx = week.days.findIndex(d => d.today)
    setSelDay(idx >= 0 ? idx : week.days.findIndex(d => !d.past) >= 0 ? week.days.findIndex(d => !d.past) : 0)
    setSelTime(null); setSelectedTeacher(null)
  }, [open, week.days])

  // Грузим реальных преподов платформы по предмету — вместе с их свободными окнами на неделю
  // (?slots=1: одна ручка вместо запроса на каждого препода по клику)
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true); setLoadError(null); setTeachers([])
    setSelectedTeacher(null)
    api.get('/api/platform/tutors', { params: {
      subject, grade: initial?.classNum || '', ...(showAllTeachers ? { all: 1 } : {}), // all=1: «показать всех» видит и неактивных (Антон 07.09)
      slots: 1, dateFrom: week.days[0].iso, dateTo: week.days[6].iso,
    } })
      .then(r => {
        if (cancelled) return
        const list = r.data.tutors || []
        setTeachers(list)
        const pk = pendingPickRef.current
        if (pk) { pendingPickRef.current = null; const t = list.find(x => x.mkId === pk.mkId); if (t) setSelectedTeacher(t) }
        else if (keepTeacherRef.current) { const t = list.find(x => x.mkId === keepTeacherRef.current); keepTeacherRef.current = null; if (t) setSelectedTeacher(t) }
      })
      .catch(e => { if (!cancelled) setLoadError(e.response?.data?.error || e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, subject, week.days])

  // При открытии окна фильтр сразу по классу из сделки (раньше всегда «Все» — в списке были все классы).
  useEffect(() => {
    if (open) { setClassFilter(classFilterFor(initial?.classNum)); setClassTouched(false) }
  }, [open, initial?.classNum])

  useEffect(() => {
    if (!open) {
      setSearch(''); setClassFilter('Все'); setClassTouched(false); setEditFilters(false); setSortKey('rank')
      setSelDay(null); setSelTime(null); setSelectedTeacher(null); setProfileTutor(null)
    }
  }, [open])

  if (!open) return null

  // Если менеджер уже предложил клиенту скидку (priceOverride выставлен на звонке
  // «Дорого») — Лидеров из списка убираем совсем: у них жёсткая цена по сетке
  // класса, скидки не бывает, предлагать их здесь нечестно по отношению к цене.
  // Плюс: менеджер в форме скидки выбирал возраст препода (priceOverrideAge) —
  // список должен показывать ТОЛЬКО преподов этого возраста, иначе можно
  // посчитать скидку «под молодого», а записать на препода 45+ лет.
  // Явно названная клиенту цена: скидка звонильщика/переписчика (priceOverride из сделки или блока записи)
  // либо цена платного урока у сопроводителя («Записать на платное»: 600 или 1200 — тоже приходит сюда,
  // Антон 28.09: «при выборе по 600 всё равно показывает и по 1200»).
  const explicitPrice = initial?.priceOverride != null && String(initial.priceOverride).trim() !== '' ? Number(initial.priceOverride) : null
  const leaderTierPrice = leaderPriceForGrade(initial?.classNum)
  // Скидка = названная цена НИЖЕ тарифа Лидеров по классу (600/700/800). Цена 1200 — не скидка, а тариф Лидеров.
  const discountActive = explicitPrice != null && leaderTierPrice != null && explicitPrice < leaderTierPrice
  const discountAgeBracket = discountActive && initial?.priceOverrideAge != null && String(initial.priceOverrideAge).trim() !== ''
    ? Number(initial.priceOverrideAge) : null

  // Зеркально: по умолчанию (без явной скидки) звонильщик всегда называет клиенту
  // тарифную цену 1200/1400/1600 (см. CallerDashboard ClientBlock, любой предмет,
  // с 13.07 не зависит от текста предмета) — значит список должен показывать
  // ТОЛЬКО внутренних преподов, обычный так дёшево стоить не может.
  // «Показать всех» (сопроводитель) отключает фильтр по группе ТОЛЬКО когда цена не названа;
  // если цена известна — группа фильтруется строго у всех ролей.
  const quotedPrice = explicitPrice != null ? explicitPrice : (showAllTeachers ? null : leaderTierPrice)
  // «Все» (пункт сортировки) — показать всех преподов независимо от группы: и Лидеров, и обычных (Роман 12.10).
  const showAll = sortKey === 'all'
  // Психологи вне тарифа Лидеров (своя цена/промо) — фильтр «только Лидеры по цене класса»
  // к ним не применяем, иначе список психологов пустой (Роман 16.10).
  const isPsychSubject = /психолог/i.test(String(subject || ''))
  const leaderOnly = !showAll && !isPsychSubject && quotedPrice != null && leaderTierPrice != null && quotedPrice >= leaderTierPrice

  // Преподы, подходящие по всему, кроме времени
  const baseTeachers = teachers.filter(t =>
    (!search || (t.name || '').toLowerCase().includes(search.toLowerCase())) &&
    (isPsychSubject || teacherMatchesClass(t, classFilter)) &&
    (!leaderOnly || t.isLeader) &&
    (showAll || !discountActive || !t.isLeader) &&
    (showAll || !discountActive || discountAgeBracket == null || teacherAgeBracket(t.age) === discountAgeBracket)
  )

  const day = selDay != null ? week.days[selDay] : null
  const slotsOf = (t) => (t.slots && day && t.slots[day.iso]) || []
  // Часы выбранного дня: объединение окон всех подходящих преподов + сколько свободны в каждый час
  const hourCounts = {}
  if (day) for (const t of baseTeachers) for (const tm of slotsOf(t)) hourCounts[tm] = (hourCounts[tm] || 0) + 1
  const hours = Object.keys(hourCounts).sort()

  // Список: только свободные в выбранное время (если время выбрано)
  let filteredTeachers = (selTime && !(selectedTeacher && !selTime)) ? baseTeachers.filter(t => slotsOf(t).includes(selTime)) : baseTeachers
  if (sortKey === 'slots') {
    const cnt = t => Object.values(t.slots || {}).reduce((s, a) => s + a.length, 0)
    filteredTeachers = [...filteredTeachers].sort((a, b) => cnt(b) - cnt(a))
  } else if (sortKey === 'price') {
    filteredTeachers = [...filteredTeachers].sort((a, b) => (a.price ?? 1e9) - (b.price ?? 1e9))
  } else if (showAllTeachers && !showAll) {
    // Сопроводителю — по числу проведённых занятий (от большего к меньшему), как раньше
    filteredTeachers = [...filteredTeachers].sort((a, b) => (b.lessonsCount || 0) - (a.lessonsCount || 0))
  }

  const pickTime = (tm) => {
    const next = selTime === tm ? null : tm
    setSelTime(next)
    if (selectedTeacher && (!next || !slotsOf(selectedTeacher).includes(next))) setSelectedTeacher(null)
  }
  const pickDay = (i) => { setSelDay(i); setSelTime(null); if (!selectedTeacher) setSelectedTeacher(null) }
  // Клик по карточке: выбрать препода (сверху откроется его неделя окон) или снять выбор
  const toggleTeacher = (t) => { setSelectedTeacher(selectedTeacher?.mkId === t.mkId ? null : t); if (selTime && !slotsOf(t).includes(selTime)) setSelTime(null) }
  // Клик по окну в неделе выбранного препода
  const pickTeacherSlot = (iso, tm) => { const i = week.days.findIndex(d => d.iso === iso); if (i >= 0) { setSelDay(i); setSelTime(tm) } }

  // Плашка в шапке: своя (платный урок) либо прежняя — названная клиенту цена.
  const badgeText = priceBadge || ((quotedPrice != null && (!showAllTeachers || explicitPrice != null)) ? `Клиенту названа цена ${quotedPrice} ₽` : null)

  const canSubmit = selectedTeacher && selTime && day
  const whenShort = day && selTime ? `${day.dow.toLowerCase()} ${selTime}` : ''
  const whenLong = day && selTime ? `${DOW_GEN[selDay]} ${day.date}, ${selTime}` : ''

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit && onSubmit({
      teacherId: selectedTeacher.id,         // "mk-N"
      teacherMkId: selectedTeacher.mkId,
      teacherName: selectedTeacher.name,
      teacherAge: selectedTeacher.age,
      teacherGender: selectedTeacher.gender,
      // Настоящая цена ЭТОГО препода (Лидер — сетка по классу, обычный — база+возраст+класс),
      // а не ручная догадка из калькулятора скидки — закрывает несостыковку возраст/цена.
      price: selectedTeacher.price,
      isLeader: !!selectedTeacher.isLeader,
      subject,
      // Явно выбранный сопроводителем уровень ОГЭ/ЕГЭ (кнопки фильтра). На бэке
      // используется как fallback, когда класс ученика в анкете не заполнен —
      // тогда группа/цена берутся по этому выбору, а не падают в базовую (Антон-баг:
      // выбрали ОГЭ, а без класса ученик садился в обычную «Математика»).
      // Только если менеджер нажал сам: фильтр, выставленный по классу из сделки, в запись не уходит — данные брони как раньше.
      level: (classTouched && (classFilter === 'ОГЭ' || classFilter === 'ЕГЭ')) ? classFilter : null,
      date: day.iso,
      time: selTime,
      lessonVariant: selectedTeacher.isLeader ? 'Пробный бесплатный' : 'Пробный платный (1 платный урок)',   // у не-Лидеров первый урок платный (Антон 04.08)
      duration: '55 мин',                     // всегда 55 мин
      comment,
      freeTrial: freeTrialOption ? freeTrial : false,
    })
  }

  // Фото препода проксируем через сам кабинет (тот же домен — грузится надёжно)
  const photoUrl = (t) => (t && t.mkId && t.photo) ? `/platform-photo/${t.mkId}` : null
  const classLabel = String(initial?.classNum || '').trim()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1080px] max-h-[92vh] flex flex-col overflow-hidden"
           onClick={e => e.stopPropagation()}>

        {/* Шапка */}
        <div className="bg-white border-b border-slate-200 px-6 py-3.5 flex items-center gap-4 z-10">
          <div className="min-w-0">
            <div className="text-[18px] font-semibold text-slate-900">{title || 'Запись на пробный урок'}</div>
            <div className="text-[12px] text-slate-500 mt-0.5">{clientLabel || '—'}</div>
          </div>
          {badgeText && (
            <div className="ml-auto text-[12.5px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
                 style={{ background: '#F0F9FF', color: '#0369A1', border: '1px solid #BAE6FD' }}>
              {badgeText}
            </div>
          )}
          <button onClick={onClose} className={(badgeText ? '' : 'ml-auto ') + 'text-slate-400 hover:text-slate-700'}><IconX size={20}/></button>
        </div>

        {/* Фильтр + неделя */}
        <div className="px-6 pt-2.5 pb-2 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-2 flex-wrap">
            <span style={PILL_BLUE}>{subject}{classFilter !== 'Все' ? ' · ' + (/^\d+$/.test(classFilter) ? classFilter + ' класс' : classFilter) : ''}</span>
            {classLabel && <span style={PILL_BLUE}>{/^\d+$/.test(classLabel) ? classLabel + ' класс' : classLabel}</span>}
            <button onClick={() => setEditFilters(v => !v)} className="text-[12px] underline" style={{ color: '#0369A1' }}>{editFilters ? 'скрыть' : 'изменить'}</button>
            <select value={sortKey} onChange={e => setSortKey(e.target.value)} style={{ ...PILL, outline: 'none', cursor: 'pointer' }}>
              {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <div className="relative">
              <IconSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск по фамилии"
                     style={{ ...PILL, paddingLeft: 26, minWidth: 170, outline: 'none', fontWeight: 500 }} />
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <button onClick={() => { keepTeacherRef.current = selectedTeacher ? selectedTeacher.mkId : null; setWeekAnchor(d => { const n = new Date(d); n.setDate(n.getDate() - 7); return n }) }}
                      className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50">
                <IconChevron size={13} className="rotate-180" />
              </button>
              <div className="px-3 py-1 rounded-full text-[12px] font-semibold"
                   style={{ background: '#F0F9FF', color: '#0369A1', border: '1px solid #BAE6FD' }}>{week.range}</div>
              <button onClick={() => { keepTeacherRef.current = selectedTeacher ? selectedTeacher.mkId : null; setWeekAnchor(d => { const n = new Date(d); n.setDate(n.getDate() + 7); return n }) }}
                      className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50">
                <IconChevron size={13} />
              </button>
            </div>
          </div>
          {editFilters && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              <span className="text-[11px] font-semibold text-slate-400 mr-0.5">Предмет:</span>
              {PLATFORM_SUBJECTS.map(s => (
                <button key={s} onClick={() => setSubject(s)} style={subject === s ? PILL_ON : PILL}>{s}</button>
              ))}
              <span className="text-[11px] font-semibold text-slate-400 ml-2 mr-0.5">Класс:</span>
              {(/^\d+$/.test(classFilterFor(initial?.classNum)) ? [classFilterFor(initial?.classNum), ...CLASS_FILTERS] : CLASS_FILTERS).map(c => (
                <button key={c} onClick={() => { setClassFilter(c); setClassTouched(true) }} style={classFilter === c ? PILL_ON : PILL}>{/^\d+$/.test(c) ? c + ' класс' : c}</button>
              ))}
            </div>
          )}

          {/* Дни недели */}
          <div className="grid mt-2.5" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 }}>
            {week.days.map((d, i) => (
              <button key={i} onClick={() => pickDay(i)} disabled={d.past}
                      className="text-center py-1.5 rounded-lg border"
                      style={selDay === i
                        ? { background: '#F0F9FF', borderColor: '#38BDF8', boxShadow: '0 0 0 1px #38BDF8 inset' }
                        : { borderColor: '#E2E8F0', background: '#fff', opacity: d.past ? 0.45 : 1, cursor: d.past ? 'default' : 'pointer' }}>
                <div className="text-[13px] font-bold text-slate-900">{d.dow[0] + d.dow.slice(1).toLowerCase()}</div>
                <div className="text-[11px] text-slate-500">{d.date}</div>
              </button>
            ))}
          </div>

          {/* Препод выбран — его свободные окна на всю неделю (как в старом окне), клик по окну выбирает день и час */}
          {selectedTeacher && (
            <div className="mt-2">
              <div className="flex items-center gap-2 text-[12px] mb-1.5">
                <span className="inline-flex items-center gap-2 font-medium px-3 py-1 rounded-full"
                      style={{ background: '#F0F9FF', color: '#0369A1', border: '1px solid #BAE6FD' }}>
                  Окошки: {selectedTeacher.name.split(' ').slice(0, 2).join(' ')}
                  <button onClick={() => setSelectedTeacher(null)} className="text-sky-700/70 hover:text-sky-900"><IconX size={12}/></button>
                </span>
                <span className="text-slate-400">Нажмите окно — день и час выберутся сразу</span>
              </div>
              <div className="grid" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 }}>
                {week.days.map((d, i) => {
                  const times = (selectedTeacher.slots && selectedTeacher.slots[d.iso]) || []
                  return (
                    <div key={i} className="flex flex-wrap gap-1 content-start" style={{ minHeight: 30 }}>
                      {times.length === 0 && <span className="w-full text-center text-[11px] text-slate-300 py-1">—</span>}
                      {times.map(tm => {
                        const on = selDay === i && selTime === tm
                        return (
                          <button key={tm} onClick={() => pickTeacherSlot(d.iso, tm)}
                                  className="text-[11px] font-medium rounded-md px-1.5"
                                  style={{ height: 26, minWidth: 46, background: on ? '#38BDF8' : '#ECFDF5', color: on ? '#fff' : '#065F46', border: '1px solid ' + (on ? '#38BDF8' : '#A7F3D0') }}>{tm}</button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          {/* Препод не выбран — часы выбранного дня + сколько преподов свободны */}
          {!selectedTeacher && (
          <div className="flex flex-wrap gap-1.5 mt-2 min-h-[38px] items-center">
            {loading && <span className="text-[12px] text-slate-400">Считаем свободные окна…</span>}
            {!loading && day && hours.length === 0 && <span className="text-[12px] text-slate-400">В этот день свободных окон нет — выберите другой день</span>}
            {!loading && hours.map(tm => {
              const on = selTime === tm
              return (
                <button key={tm} onClick={() => pickTime(tm)}
                        className="text-center rounded-lg px-2 py-1"
                        style={{ minWidth: 64, border: '1px solid ' + (on ? '#38BDF8' : '#A7F3D0'), background: on ? '#38BDF8' : '#ECFDF5', color: on ? '#fff' : '#065F46' }}>
                  <div className="text-[12px] font-semibold leading-tight">{tm}</div>
                  <div className="text-[10.5px] leading-tight" style={{ color: on ? '#E0F2FE' : '#047857' }}>{hourCounts[tm]}</div>
                </button>
              )
            })}
          </div>
          )}
          {discountActive && (
            <div className="pt-1.5 text-[11.5px]" style={{ color: '#B45309' }}>
              Клиенту предложена скидка — часть преподавателей скрыта (им нельзя дать скидку)
              {discountAgeBracket != null && <> · показаны только преподаватели {AGE_BRACKET_LABELS[discountAgeBracket]}</>}
            </div>
          )}
          {leaderOnly && (
            <div className="pt-1.5 text-[11.5px]" style={{ color: '#0369A1' }}>
              Клиенту названа цена {leaderTierPrice} ₽ по классу — показаны только преподаватели, которые могут вести по этой цене
            </div>
          )}
        </div>

        {/* Преподаватели */}
        <div className="flex-1 overflow-y-auto px-6 py-3.5" style={{ background: '#fff' }}>
          {!loading && !loadError && teachers.length > 0 && (
            <div className="text-[12px] text-slate-500 mb-2.5">
              {selTime
                ? <>Свободны в {whenLong}: <b className="text-slate-900">{filteredTeachers.length}</b> из {baseTeachers.length}</>
                : <>Нажмите час выше — покажем, кто свободен в это время. Или нажмите преподавателя — сверху откроются его окна. Всего преподавателей: <b className="text-slate-900">{baseTeachers.length}</b></>}
            </div>
          )}
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            {filteredTeachers.map(t => (
              <TutorPickerCard key={t.mkId} t={t} selected={selectedTeacher?.mkId === t.mkId} showFirstLessonLabel
                actionLabel={selTime ? `Выбрать · ${whenShort}` : 'Показать окна'}
                onClick={() => toggleTeacher(t)}
                onProfile={() => setProfileTutor(t)} />
            ))}
          </div>
          {loading && <div className="px-4 py-6 text-[12px] text-slate-400 text-center">Загрузка преподавателей…</div>}
          {loadError && <div className="px-4 py-6 text-[12px] text-rose-600 text-center">Ошибка: {loadError}</div>}
          {!loading && !loadError && filteredTeachers.length === 0 && (
            <div className="px-4 py-6 text-[12px] text-slate-400 text-center">
              {selTime ? 'В это время свободных преподавателей нет — выберите другой час' : 'Преподаватели не найдены'}
            </div>
          )}
        </div>

        {/* Футер: комментарий + кнопки (длительность 55 мин, вариант пробный — автоматом) */}
        <div className="border-t border-slate-200 bg-white px-6 py-3.5 flex items-end gap-4">
          <label className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Комментарий</div>
            <input type="text" value={comment} onChange={e => setComment(e.target.value)} placeholder="Пожелания клиента"
                   className="w-full border border-slate-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-sky-400"/>
          </label>
          <div className="text-[12px] text-slate-600 pb-2.5 whitespace-nowrap">
            {canSubmit
              ? <>Выбран(а) <b className="text-slate-900">{selectedTeacher.name.split(' ').slice(0, 2).join(' ')}</b> · {whenLong}{selectedTeacher.price != null ? ` · ${selectedTeacher.price} ₽` : ''}</>
              : selTime ? 'Теперь выберите преподавателя' : 'Выберите день и час'}
          </div>
          {freeTrialOption && isPsychSubject && (
            <label className="flex items-center gap-1.5 pb-2.5 text-[12px] text-slate-700 whitespace-nowrap cursor-pointer select-none">
              <input type="checkbox" checked={freeTrial} onChange={e => setFreeTrial(e.target.checked)} className="accent-emerald-600"/>
              Пробное бесплатно
            </label>
          )}
          <div className="flex items-center gap-2 pb-[2px]">
            <button onClick={onClose}
                    className="bg-white border border-slate-300 text-slate-700 px-5 py-2.5 rounded-lg text-[13px] hover:bg-slate-50">Отмена</button>
            <button onClick={handleSubmit} disabled={!canSubmit}
                    className="text-white px-5 py-2.5 rounded-lg text-[13px] font-semibold flex items-center gap-2"
                    style={{ background: '#10B981', opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
              {/* У платформенного (не-Лидера) пробных нет — первый урок сразу платный,
                  поэтому и кнопка честная: «Записать на урок» (Антон 01.09). */}
              {submitLabel || (!selectedTeacher ? 'Записать' : selectedTeacher.isLeader ? 'Записать на пробный' : 'Записать на урок')} <IconCheck size={14} strokeWidth={2.5}/>
            </button>
          </div>
        </div>
      </div>

      {/* Карточка препода */}
      {profileTutor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.55)' }}
             onClick={() => setProfileTutor(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[560px] max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 flex gap-4 border-b border-slate-100">
              {photoUrl(profileTutor)
                ? <img src={photoUrl(profileTutor)} alt="" className="w-20 h-20 rounded-xl object-cover" style={{ background: '#F1F5F9' }}/>
                : <div className="w-20 h-20 rounded-xl flex items-center justify-center text-slate-400" style={{ background: '#F1F5F9' }}><IconUser size={28}/></div>}
              <div className="min-w-0">
                <div className="text-[16px] font-semibold text-slate-900">{profileTutor.name}</div>
                <div className="text-[12px] text-slate-500 mt-1">
                  {profileTutor.age != null && `${profileTutor.age} ${pluralRu(profileTutor.age, 'год', 'года', 'лет')} · `}{profileTutor.subject}
                  {profileTutor.price != null && ` · ${profileTutor.price} ₽ / урок`}
                </div>
                <div className="text-[12px] text-slate-500 mt-1">{profileTutor.classes || ''}</div>
                {profileTutor.experience && <div className="text-[12px] text-slate-500 mt-0.5">Опыт: {profileTutor.experience}</div>}
                {formatTeacherPhone(profileTutor.phone) && <a href={telHref(profileTutor.phone)} className="block text-[12.5px] font-bold text-slate-700 mt-1">{formatTeacherPhone(profileTutor.phone)}</a>}
              </div>
              <button onClick={() => setProfileTutor(null)} className="ml-auto text-slate-400 hover:text-slate-700"><IconX size={18}/></button>
            </div>
            {profileTutor.rank && (
              <div className="px-5 pt-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">За последние 90 дней</div>
                <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                  {[
                    ['Свободных окон на этой неделе', profileTutor.rank.free_slots],
                    ['Остались после первого урока', profileTutor.rank.conv_came > 0 ? `${profileTutor.rank.conv_stayed} из ${profileTutor.rank.conv_came}` : 'первых уроков ещё не было'],
                    ['Учеников сейчас', profileTutor.rank.students],
                    ['Уроков за 30 дней', profileTutor.rank.lessons_30d || 0],
                    ['Обычно отвечает на предложение', profileTutor.rank.resp_min != null ? `${profileTutor.rank.resp_min} мин` : '—'],
                    ['Поздние отмены и переносы', profileTutor.rank.penalty > 0 ? 'бывают' : 'нет'],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-xl px-3 py-2" style={{ background: '#F8FAFC', border: '1px solid #EEF2F7' }}>
                      <div className="text-[15px] font-extrabold text-slate-900">{v}</div>
                      <div className="text-[11px] text-slate-500">{k}</div>
                    </div>
                  ))}
                </div>
                {profileTutor.rank.crm_floor && <div className="text-[12px] font-semibold mt-2" style={{ color: '#98A2B3' }}>{profileTutor.rank.crm_floor}</div>}
              </div>
            )}
            {profileTutor.otbor && profileTutor.otbor.note && (
              <div className="px-5 pt-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Заметка сопровождения</div>
                <div className="text-[13px] text-slate-700">{profileTutor.otbor.note}</div>
              </div>
            )}
            {profileTutor.desc && (
              <div className="p-5 text-[13px] text-slate-700 whitespace-pre-line leading-relaxed">{profileTutor.desc}</div>
            )}
            <div className="p-5 pt-3 flex gap-2">
              <button onClick={() => { setSelectedTeacher(profileTutor); if (selTime && !slotsOf(profileTutor).includes(selTime)) setSelTime(null); setProfileTutor(null) }}
                      className="flex-1 text-white px-4 py-2.5 rounded-lg text-[13px] font-semibold" style={{ background: '#38BDF8' }}>
                {selTime && slotsOf(profileTutor).includes(selTime) ? `Выбрать · ${whenShort}` : 'Выбрать и показать окна'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
